/**
 * Parse a YYYY-MM-DD calendar date into a Date at LOCAL midnight.
 *
 * Why this exists: date-fns `parseISO("2026-05-01")` (a date-only ISO
 * string) returns UTC midnight per the ISO 8601 spec. In any timezone
 * west of UTC that becomes the *previous* calendar day in local time,
 * which silently breaks `startOfDay`, `isToday`, and date-range loops.
 * (This is the bug that hid every staff member's shifts on Beerfest
 * opening night — Calgary is UTC-6, so May 2 UTC = May 1 18:00 local
 * = May 1 after startOfDay.)
 *
 * Input is the YYYY-MM-DD prefix only — we slice off any trailing
 * timestamp/zone the DB might attach.
 */
export function parseLocalDate(value: string): Date {
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/**
 * Null-returning variant of `parseLocalDate` for nullable DB columns.
 *
 * Parses the YYYY-MM-DD prefix of a timestamptz string into a Date at LOCAL
 * midnight. `new Date("2026-05-02 00:00:00+00")` is UTC midnight which
 * becomes the previous calendar day in any timezone west of UTC — that's
 * what was labeling live Calgary events as "past".
 *
 * Unlike `parseLocalDate` (which tolerates a missing month/day by defaulting
 * them to 1), this returns null for empty input or anything short of a full
 * year-month-day.
 */
export function parseLocalDateOrNull(value: string): Date | null {
  if (!value) return null;
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
